<template>
	<section class="goods-section">
		<div class="goods-container">
			<div class="section-intro center">
				<h2 class="section-title">{{ $t('cargoTypes.title') }}</h2>
				<p class="section-subtitle">{{ $t('cargoTypes.subtitle') }}</p>
			</div>

			<div class="goods-cards">
				<div class="left-column">
					<!-- 普通货物卡片 -->
					<div class="goods-card general">
						<div class="card-top">
							<div class="card-icon">
								<i class="fas fa-box-open"></i>
							</div>
							<h3 class="card-title">{{ $t('cargoTypes.general.title') }}</h3>
							<p class="card-desc">{{ $t('cargoTypes.general.description') }}</p>
						</div>
						<div class="card-content">
							<p class="content-text">{{ $t('cargoTypes.general.commonItems') }}</p>
							<div class="goods-tags">
								<span v-for="(tag, index) in $t('cargoTypes.general.tags')" :key="`tag-${index}`" class="tag"
									:style="{ borderColor: tagColors[index] }">
									{{ tag }}
								</span>
							</div>
						</div>
					</div>

					<!-- 特殊货物卡片 -->
					<div class="goods-card special">
						<div class="card-top">
							<div class="card-icon">
								<i class="fas fa-star"></i>
							</div>
							<h3 class="card-title" :title="$t('cargoTypes.special.title')">{{ $t('cargoTypes.special.title') }}</h3>
							<p class="card-desc">{{ $t('cargoTypes.special.description') }}</p>
						</div>
						<div class="card-content">
							<div class="special-grid">
								<div v-for="(item, index) in $t('cargoTypes.special.items')" :key="`special-${index}`"
									class="special-item" :style="{ '--special-color': specialColors[index] }">
									<div class="item-icon">
										<i :class="item.icon"></i>
									</div>
									<div>
										<span class="item-name">{{ item.name }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- 危险品卡片 -->
				<div class="goods-card dangerous">
					<div class="card-top">
						<div class="card-icon">
							<i class="fas fa-radiation-alt"></i>
						</div>
						<h3 class="card-title">{{ $t('cargoTypes.dangerous.title') }}</h3>
						<p class="card-desc">{{ $t('cargoTypes.dangerous.description') }}</p>
					</div>

					<div class="card-content" style="padding-bottom: 0;">
						<div class="danger-grid">
							<div v-for="(classItem, index) in $t('cargoTypes.dangerous.classes')" :key="`danger-${index}`"
								class="danger-item" :style="{ '--danger-color': dangerColors[index] }">
								<div class="danger-class">
									<span class="class-number" :title="classItem.class">{{ classItem.class }}</span>
									<span class="class-name">{{ classItem.name }}</span>
								</div>

								<div class="danger-info">
									<template v-if="classItem.subclasses">
										<div class="subclass-container">
											<div v-for="(sub, subIndex) in classItem.subclasses" :key="`subclass-${subIndex}`"
												class="subclass-item">
												<div class="subclass-header">
													<span class="subclass-label" :title="sub.subclass">{{ sub.subclass }}</span>
													<span class="subclass-name">{{ sub.name }}</span>
												</div>

												<div class="subclass-details">
													<p v-if="sub.example" class="detail-item">
														<i class="fas fa-box-open detail-icon"></i>
														<span class="detail-label">{{ $t('common.example') }}:</span>
														<span class="detail-value">{{ sub.example }}</span>
													</p>
												</div>
											</div>
										</div>
									</template>

									<template v-else>
										<div class="no-subclass-details">
											<p v-if="classItem.example" class="detail-item">
												<i class="fas fa-box-open detail-icon"></i>
												<span class="detail-label">{{ $t('common.example') }}:</span>
												<span class="detail-value">{{ classItem.example }}</span>
											</p>
											<p v-if="classItem.note" class="detail-item">
												<i class="fas fa-info-circle detail-icon"></i>
												<span class="detail-value">{{ classItem.note }}</span>
											</p>
										</div>
									</template>
								</div>
							</div>
						</div>

						<div class="danger-notice">
							<h4>{{ $t('cargoTypes.dangerous.notes.title') }}</h4>
							<ul class="notice-list">
								<template v-for="(note, noteIndex) in $t('cargoTypes.dangerous.notes.items')">
									<li :key="`note-${noteIndex}`" class="notice-item main-item">
										<i class="fas fa-exclamation-circle notice-icon"></i>
										<span>{{ note.title || note }}</span>
									</li>
									<ul v-if="note.items" class="sub-notice-list">
										<li v-for="(subNote, subIndex) in note.items" :key="`sub-note-${subIndex}`"
											class="notice-item sub-item">
											<i class="fas fa-circle sub-notice-icon"></i>
											<span>{{ subNote }}</span>
										</li>
									</ul>
								</template>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
	export default {
		name: 'OperableGoods',
		data() {
			return {
				tagColors: ['#4a90e2', '#357ABD', '#2F80ED', '#3B5998', '#1C3D7B'],
				dangerColors: [
					'#3a8edc', '#3399cc', '#66ccff', '#0099cc',
					'#007acc', '#004c99', '#5a5aff', '#4b6cb7',
					'#2e86de', '#4056f4', '#007acc', '#3a8edc'
				],
				specialColors: [
					'#0a74da', '#1366d6', '#3b5998', '#2a4d69',
					'#1e3d7a', '#134e7a', '#1a4b6c', '#2a4d69'
				],
				scale: 1
			}
		},
		mounted() {
			window.addEventListener('resize', this.handleResize);
			this.handleResize();
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.handleResize);
		},
		methods: {
			handleResize() {
				const zoomLevel = window.devicePixelRatio || 1;
				if (zoomLevel !== 1) {
					console.log(`Detected zoom level: ${zoomLevel}`);
				}
				this.scale = Math.min(1, 1 / zoomLevel);

				// 根据缩放级别调整布局
				if (zoomLevel > 1.5) {
					document.documentElement.style.setProperty('--base-font-scale', '0.9');
				} else {
					document.documentElement.style.setProperty('--base-font-scale', '1');
				}
			}
		}
	}
</script>

<style lang="scss">
	* {
		word-break: break-word;
		overflow-wrap: anywhere;
	}

	:root[chinese] {
		--base-font-scale: 1;
	}

	:root[english] {
		--base-font-scale: 0.92;
	}

	.goods-section {
		padding: calc(2rem * var(--base-font-scale)) calc(1rem * var(--base-font-scale));
		background: #f8fafc;
		width: 100%;
		box-sizing: border-box;
		zoom: 1;
		text-size-adjust: 100%;
		-webkit-text-size-adjust: 100%;
		-moz-text-size-adjust: 100%;
		-ms-text-size-adjust: 100%;
	}

	.goods-container {
		max-width: calc(1200px * var(--base-font-scale));
		margin: 0 auto;
		width: 100%;
		padding: 0 calc(15px * var(--base-font-scale));
		box-sizing: border-box;
	}

	.section-intro {
		margin-bottom: calc(80px * var(--base-font-scale));

		&.center {
			text-align: center;
		}

		.section-title {
			font-size: clamp(1.8rem, 4vw, 2.625rem);
			font-weight: 600;
			margin-bottom: calc(15px * var(--base-font-scale));
			color: #1a3e72;
			position: relative;
			display: inline-block;

			&::after {
				content: '';
				position: absolute;
				bottom: calc(-15px * var(--base-font-scale));
				left: 50%;
				transform: translateX(-50%);
				width: calc(80px * var(--base-font-scale));
				height: calc(4px * var(--base-font-scale));
				background: linear-gradient(to right, #1a6fdf, #4facfe);
				border-radius: calc(2px * var(--base-font-scale));
			}
		}

		.section-subtitle {
			font-size: clamp(1rem, 1.5vw, 1.125rem);
			color: #6c757d;
			font-weight: 300;
			letter-spacing: calc(1px * var(--base-font-scale));
			max-width: calc(800px * var(--base-font-scale));
			margin: 0 auto;
		}
	}

	.goods-cards {
		display: grid;
		grid-template-columns: 0.7fr 1.8fr;
		gap: calc(2rem * var(--base-font-scale));
		width: 100%;
		min-width: 0;

		@media (max-width: 992px) {
			grid-template-columns: 1fr;
		}
	}

	.left-column {
		display: flex;
		flex-direction: column;
		gap: calc(2rem * var(--base-font-scale));
		min-width: 0;

		@media (max-width: 992px) {
			margin-bottom: calc(2rem * var(--base-font-scale));
		}
	}

	.goods-card {
		background: white;
		border-radius: calc(12px * var(--base-font-scale));
		box-shadow: 0 calc(10px * var(--base-font-scale)) calc(20px * var(--base-font-scale)) rgba(0, 0, 0, 0.08);
		overflow: hidden;
		transition: transform 0.3s ease, box-shadow 0.3s ease;
		padding-bottom: calc(15px * var(--base-font-scale));
		width: 100%;
		box-sizing: border-box;
		min-width: 0;

		&:hover {
			transform: translateY(calc(-5px * var(--base-font-scale)));
			box-shadow: 0 calc(15px * var(--base-font-scale)) calc(30px * var(--base-font-scale)) rgba(0, 0, 0, 0.12);
		}
	}

	.card-top {
		padding: calc(1.5rem * var(--base-font-scale));
		position: relative;
		color: white;

		.card-icon {
			width: calc(50px * var(--base-font-scale));
			height: calc(50px * var(--base-font-scale));
			background: rgba(255, 255, 255, 0.2);
			border-radius: 50%;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: calc(1.5rem * var(--base-font-scale));
			margin-bottom: calc(1rem * var(--base-font-scale));
		}

		.card-title {
			font-size: clamp(1.25rem, 2vw, 1.5rem);
			margin-bottom: calc(0.5rem * var(--base-font-scale));
			font-weight: 600;

			/* 多行省略专用 */
			display: -webkit-box;
			-webkit-line-clamp: 2;
			/* 最多两行 */
			-webkit-box-orient: vertical;

			/* 允许换行 + 省略 */
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: normal;
			/* 允许换行 */

			/* 防止英文挤出容器 */
			word-break: break-word;
			overflow-wrap: break-word;
		}


		.card-desc {
			font-size: clamp(0.85rem, 1.2vw, 0.95rem);
			opacity: 0.9;
			margin: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}
	}

	.general .card-top {
		background: linear-gradient(135deg, #4361ee, #3a0ca3);
	}

	.dangerous .card-top {
		background: linear-gradient(135deg, #3a86ff, #7209b7);
	}

	.special .card-top {
		background: linear-gradient(135deg, #4cc9f0, #4895ef);
	}

	.card-content {
		padding: calc(1.5rem * var(--base-font-scale));
		min-width: 0;

		.content-text {
			color: #6c757d;
			margin-bottom: calc(1.5rem * var(--base-font-scale));
			line-height: 1.6;
			font-size: clamp(0.875rem, 1.1vw, 1rem);
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
		}
	}

	.goods-tags {
		display: flex;
		flex-wrap: wrap;
		gap: calc(0.75rem * var(--base-font-scale));
		white-space: nowrap;
		overflow-x: auto;
		padding-bottom: 5px;

		&::-webkit-scrollbar {
			height: 3px;
		}

		&::-webkit-scrollbar-thumb {
			background: rgba(0, 0, 0, 0.1);
		}

		.tag {
			padding: calc(0.5rem * var(--base-font-scale)) calc(1rem * var(--base-font-scale));
			border-radius: calc(20px * var(--base-font-scale));
			font-size: clamp(0.5rem, 1vw, 0.75rem);
			font-weight: 500;
			background-color: white;
			border: calc(2px * var(--base-font-scale)) solid;
			transition: all 0.2s ease;
			white-space: nowrap;
			flex-shrink: 0;

			&:hover {
				transform: translateY(calc(-2px * var(--base-font-scale)));
				box-shadow: 0 calc(3px * var(--base-font-scale)) calc(10px * var(--base-font-scale)) rgba(0, 0, 0, 0.1);
			}
		}
	}

	.special-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(calc(120px * var(--base-font-scale)), 1fr));
		gap: calc(1rem * var(--base-font-scale));

		.special-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			padding: calc(1rem * var(--base-font-scale)) calc(0.5rem * var(--base-font-scale));
			border-radius: calc(8px * var(--base-font-scale));
			background: #f8f9fa;
			transition: all 0.3s ease;
			cursor: default;
			min-width: 0;
			min-height: calc(100px * var(--base-font-scale));

			&:hover {
				background: var(--special-color);
				color: white;
				transform: translateY(calc(-5px * var(--base-font-scale)));
				box-shadow: 0 calc(5px * var(--base-font-scale)) calc(15px * var(--base-font-scale)) rgba(0, 0, 0, 0.1);

				.item-icon {
					color: white;
					transform: scale(1.1);
				}

				.item-name {
					color: white;
				}
			}

			.item-icon {
				font-size: clamp(1.5rem, 2vw, 1.75rem);
				margin-bottom: calc(0.5rem * var(--base-font-scale));
				color: var(--special-color);
				transition: all 0.3s ease;
			}

			.item-name {
				font-size: clamp(0.8rem, 1vw, 0.9rem);
				font-weight: 500;
				color: #2b2d42;
				text-align: center;
				transition: color 0.3s ease;
				word-break: break-word;
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical;
			}
		}
	}

	.danger-grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: calc(1.5rem * var(--base-font-scale));
		margin-bottom: calc(2rem * var(--base-font-scale));
		min-width: 0;

		@media (max-width: 992px) {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		@media (max-width: 768px) {
			grid-template-columns: 1fr;
			min-width: 100%;
		}
	}

	.danger-item {
		background: white;
		border-radius: calc(10px * var(--base-font-scale));
		overflow: hidden;
		box-shadow: 0 calc(3px * var(--base-font-scale)) calc(12px * var(--base-font-scale)) rgba(0, 0, 0, 0.08);
		border-left: calc(5px * var(--base-font-scale)) solid var(--danger-color);
		transition: all 0.3s ease;
		min-width: 0;
		min-height: calc(150px * var(--base-font-scale));

		&:hover {
			transform: translateY(calc(-5px * var(--base-font-scale)));
			box-shadow: 0 calc(8px * var(--base-font-scale)) calc(20px * var(--base-font-scale)) rgba(0, 0, 0, 0.12);
		}
	}

	.danger-class {
		background-color: var(--danger-color);
		color: white;
		padding: calc(0.5rem * var(--base-font-scale)) calc(0.7rem * var(--base-font-scale));
		display: flex;
		align-items: center;
		gap: calc(0.4rem * var(--base-font-scale));

		.class-number {
			width: 90%;
			font-weight: 700;
			font-size: clamp(0.8rem, 1vw, 1rem);
			background: rgba(255, 255, 255, 0.2);
			padding: calc(0.2rem * var(--base-font-scale)) calc(0.4rem * var(--base-font-scale));
			border-radius: calc(4px * var(--base-font-scale));
			flex-shrink: 1;
			/* 允许收缩 */
			// white-space: normal;
			white-space: pre-line;
			/* 默认换行 */
			word-break: break-word;
			/* 单词/字符太长时自动换行 */
		}

		.class-name {
			font-weight: 600;
			font-size: clamp(0.8rem, 0.95vw, 0.95rem);
			flex: 1;
			min-width: 0;
			word-break: break-word;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}
	}

	.danger-info {
		padding: calc(0.6rem * var(--base-font-scale));
	}

	.subclass-container {
		display: flex;
		flex-direction: column;
		gap: calc(1rem * var(--base-font-scale));
	}

	.subclass-item {
		background: #f8fafc;
		border-radius: calc(6px * var(--base-font-scale));
		padding: calc(0.8rem * var(--base-font-scale));
		border-left: calc(3px * var(--base-font-scale)) solid var(--danger-color);
	}

	.subclass-header {
		display: flex;
		align-items: flex-start;
		gap: calc(0.5rem * var(--base-font-scale));
		margin-bottom: calc(0.5rem * var(--base-font-scale));

		.subclass-label {
			flex: 0 1 100%;
			font-weight: 600;
			color: var(--danger-color);
			font-size: clamp(0.6rem, 1vw, 0.85rem);
			// flex-shrink: 0;
			white-space: normal;
			overflow-wrap: anywhere;
		}


		.subclass-name {
			font-weight: 500;
			font-size: clamp(0.85rem, 1vw, 0.9rem);
			color: #2d3748;
			word-break: break-word;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}
	}

	.detail-item {
		display: flex;
		align-items: flex-start;
		gap: calc(0.5rem * var(--base-font-scale));
		margin: calc(0.4rem * var(--base-font-scale)) 0;
		font-size: clamp(0.8rem, 1vw, 0.85rem);
		line-height: 1.5;

		.detail-icon {
			color: var(--danger-color);
			font-size: calc(0.8rem * var(--base-font-scale));
			margin-top: calc(0.15rem * var(--base-font-scale));
			flex-shrink: 0;
		}

		.detail-label {
			font-weight: 500;
			color: #4a5568;
			flex-shrink: 0;
		}

		.detail-value {
			color: #718096;
			word-break: break-word;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}
	}

	.no-subclass-details {
		padding: calc(0.5rem * var(--base-font-scale)) 0;
	}

	.danger-notice {
		// background: #fef6f6;
		border-radius: calc(8px * var(--base-font-scale));
		padding: calc(1.2rem * var(--base-font-scale));
		border-left: calc(4px * var(--base-font-scale)) solid #e53e3e;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	}

	.danger-notice h4 {
		font-size: clamp(1rem, 1.2vw, 1.1rem);
		margin: 0 0 calc(1rem * var(--base-font-scale));
		color: #c53030;
		display: flex;
		align-items: center;
		gap: calc(0.5rem * var(--base-font-scale));
		font-weight: 600;
	}

	.danger-notice h4::before {
		content: '⚠️';
		font-size: 1.2em;
	}

	.notice-list {
		margin: 0;
		padding-left: 0;
		list-style: none;
	}

	.notice-item {
		display: flex;
		gap: calc(0.6rem * var(--base-font-scale));
		font-size: clamp(0.85rem, 1vw, 0.9rem);
		color: #4a5568;
		margin-bottom: calc(0.4rem * var(--base-font-scale));
		line-height: 1.5;
		align-items: flex-start;
	}

	.main-item {
		font-weight: 500;
		color: #2d3748;
	}

	.sub-notice-list {
		padding-left: calc(1.5rem * var(--base-font-scale));
		margin: 0 0 calc(0.8rem * var(--base-font-scale)) 0;
		list-style: none;
	}

	.sub-item {
		color: #718096;
		position: relative;
		padding-left: calc(0.5rem * var(--base-font-scale));
	}

	.notice-icon {
		color: #e53e3e;
		font-size: calc(0.9rem * var(--base-font-scale));
		margin-top: calc(0.15rem * var(--base-font-scale));
		flex-shrink: 0;
	}

	.sub-notice-icon {
		color: #a0aec0;
		font-size: calc(0.5rem * var(--base-font-scale));
		margin-top: calc(0.5rem * var(--base-font-scale));
		flex-shrink: 0;
	}

	.notice-item span {
		word-break: break-word;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
	}

	@media (max-width: 480px) {
		.section-intro {
			margin-bottom: calc(40px * var(--base-font-scale));
		}

		.special-grid {
			grid-template-columns: repeat(2, 1fr);
		}

		.goods-tags {
			justify-content: flex-start;
		}
	}

	/* 防止缩放导致布局错乱 */
	@media screen and (max-resolution: 1dppx) {
		:root {
			--base-font-scale: 1;
		}
	}

	@media screen and (min-resolution: 1.1dppx) {
		:root {
			--base-font-scale: 0.95;
		}
	}

	@media screen and (min-resolution: 1.5dppx) {
		:root {
			--base-font-scale: 0.9;
		}
	}
</style>